/**
 * AlertFormDialog — create / edit a metric alert (Story 27.2).
 *
 * §4.1/§4.2 centered modal Dialog (NEVER a Sheet), cloned from GoalFormDialog:
 * Radix dialog, motion overlay bg-black/35, rounded-3xl p-8 max-w-md, staggered
 * fields, reset-on-OPEN (§4.6), close blocked while submitting.
 *
 * Field flow (epics.md AC):
 *   Client (required) → Platform (this client's connected integrations) →
 *   Metric (filtered by platform via the 5.35 registry) → Condition →
 *   Threshold → Interval → auto-generated editable name "{Platform} {Metric} Alert".
 *
 * Soft duplicate notice (non-blocking): when client+platform+metric+condition+
 * threshold+interval already exists (in the loaded `alerts` list), a warning is
 * shown. Duplicates are ALLOWED (27.1 decision #3) — never a hard block.
 *
 * Saving POSTs (create) or PATCHes (edit) /api/alerts; a 403 UpgradeRequiredError
 * surfaces the UpgradeLimitModal (defense-in-depth; the page also guards the
 * Create button with useUpgradeGuard).
 */

import { useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Button, Input, Label } from "@provenleap/ui";
import { motion, AnimatePresence } from "motion/react";
import { Warning } from "@phosphor-icons/react";
import {
  PLAN_LIMITS,
  SAFETY_CAPS,
  getMinimumPlanForFeature,
  type Plan,
} from "@provenleap/types";
import { apiPost, apiPatch } from "../../lib/apiClient";
import { useClientIntegrations } from "../../hooks/useClientIntegrations";
import { useAvailableMetrics } from "../../hooks/useAvailableMetrics";
import { usePlanLimits } from "../../hooks/usePlanLimits";
import { UpgradeLimitModal } from "../billing/UpgradeLimitModal";
import { DialogSelect } from "../clients/DialogSelect";
import { countAlertsForGate } from "../../lib/alertPlanGate";
import {
  CONDITION_OPTIONS,
  INTERVAL_OPTIONS,
  platformDisplayName,
  platformLogoUrl,
  humanizeMetricKey,
} from "./alertDisplay";
import type { Alert, AlertCondition, AlertInterval } from "./types";

export interface AlertFormClientOption {
  id: string;
  name: string;
  /** Drives the per-client gate's demo exclusion (server getAlertCount parity). */
  demoMode?: boolean;
}

interface AlertGateModalState {
  currentPlan: Plan;
  currentUsage: number;
  currentLimit: number;
  upgradePlan?: Plan;
  nextTierLimit: number | null;
}

interface UpgradeProblem {
  type?: string;
  current_plan?: Plan;
  current_limit?: number;
  current_usage?: number;
  upgrade_to?: Plan;
  next_tier_limit?: number | null;
}

function isUpgradeProblem(
  err: unknown,
): err is { status: number } & UpgradeProblem {
  return (
    typeof err === "object" &&
    err !== null &&
    (err as { status?: number }).status === 403 &&
    (err as { type?: string }).type ===
      "https://provenleap.com/errors/upgrade-required"
  );
}

function gateFromProblem(p: UpgradeProblem): AlertGateModalState {
  return {
    currentPlan: p.current_plan ?? "free",
    currentUsage: p.current_usage ?? 0,
    currentLimit: p.current_limit ?? 0,
    upgradePlan: p.upgrade_to,
    nextTierLimit: p.next_tier_limit ?? null,
  };
}

/** Per-tenant/-client gate state (mirrors the server feature-guard math). */
function buildGate(plan: Plan, count: number): AlertGateModalState | null {
  const limit = PLAN_LIMITS[plan].alertLimit;
  if (limit === null) {
    const cap = SAFETY_CAPS.alertLimit;
    if (cap !== undefined && count >= cap) {
      return {
        currentPlan: plan,
        currentUsage: count,
        currentLimit: cap,
        upgradePlan: undefined,
        nextTierLimit: null,
      };
    }
    return null;
  }
  if (count < limit) return null;
  const upgrade = getMinimumPlanForFeature("alertLimit", count + 1);
  const isMax = upgrade === plan;
  const nextLimit = isMax ? null : PLAN_LIMITS[upgrade].alertLimit;
  return {
    currentPlan: plan,
    currentUsage: count,
    currentLimit: limit,
    upgradePlan: isMax ? undefined : upgrade,
    nextTierLimit: typeof nextLimit === "number" ? nextLimit : null,
  };
}

interface AlertFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Active clients for the required Client selector. */
  clients: AlertFormClientOption[];
  /** When set, the dialog is in EDIT mode and pre-fills from this alert. */
  alert?: Alert | null;
  /**
   * The loaded alerts (for the soft duplicate notice). The dialog warns — never
   * blocks — when an identical rule already exists.
   */
  existingAlerts?: Alert[];
  onSaved?: () => void;
}

interface FormState {
  clientId: string;
  platform: string;
  metricKey: string;
  condition: AlertCondition;
  threshold: string;
  interval: AlertInterval;
  name: string;
}

function emptyState(): FormState {
  return {
    clientId: "",
    platform: "",
    metricKey: "",
    condition: "gt",
    threshold: "",
    interval: "day",
    name: "",
  };
}

function stateFromAlert(alert: Alert): FormState {
  return {
    clientId: alert.clientId,
    platform: alert.platform,
    metricKey: alert.metricKey,
    condition: alert.condition,
    threshold: String(alert.threshold),
    interval: alert.interval,
    name: alert.name,
  };
}

export function AlertFormDialog({
  open,
  onOpenChange,
  clients,
  alert,
  existingAlerts = [],
  onSaved,
}: AlertFormDialogProps) {
  const queryClient = useQueryClient();
  const { plan } = usePlanLimits();
  const isEdit = !!alert;
  const [form, setForm] = useState<FormState>(() => emptyState());
  // Tracks whether the user manually edited the name — so the auto-name never
  // clobbers a hand-typed value.
  const [nameDirty, setNameDirty] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [gateModal, setGateModal] = useState<AlertGateModalState | null>(null);

  // Reset on OPEN (never on close — §4.6).
  useEffect(() => {
    if (!open) return;
    setForm(alert ? stateFromAlert(alert) : emptyState());
    setNameDirty(!!alert); // editing → keep the existing name as-is
    setErrors({});
  }, [open, alert]);

  const effectiveClientId = form.clientId || undefined;
  const { integrations } = useClientIntegrations(effectiveClientId);
  const { allMetrics } = useAvailableMetrics(effectiveClientId);

  const platformOptions = useMemo(
    () => Array.from(new Set(integrations.map((i) => i.type))),
    [integrations],
  );
  const metricOptions = useMemo(
    () => (form.platform ? allMetrics.filter((m) => m.platform === form.platform) : []),
    [allMetrics, form.platform],
  );

  const clientSelectOptions = useMemo(
    () => clients.map((cl) => ({ value: cl.id, label: cl.name })),
    [clients],
  );
  const platformSelectOptions = useMemo(() => {
    const emptyLabel = !effectiveClientId
      ? "Select a client first…"
      : platformOptions.length === 0
        ? "No connected platforms for this client"
        : "Select a platform…";
    return [
      { value: "", label: emptyLabel, disabled: true },
      ...platformOptions.map((p) => {
        const url = platformLogoUrl(p);
        return {
          value: p,
          label: platformDisplayName(p),
          iconUrl: url,
        };
      }),
    ];
  }, [platformOptions, effectiveClientId]);
  const metricSelectOptions = useMemo(
    () => metricOptions.map((m) => ({ value: m.key, label: m.label ?? m.key })),
    [metricOptions],
  );

  // Auto-generated default name "{Platform} {Metric} Alert" — applied whenever
  // platform/metric change AND the user has not manually edited the name.
  useEffect(() => {
    if (nameDirty) return;
    if (!form.platform || !form.metricKey) return;
    const auto = `${platformDisplayName(form.platform)} ${humanizeMetricKey(form.metricKey)} Alert`;
    setForm((prev) => (prev.name === auto ? prev : { ...prev, name: auto }));
  }, [form.platform, form.metricKey, nameDirty]);

  function patch(next: Partial<FormState>) {
    setForm((prev) => ({ ...prev, ...next }));
  }

  // Changing the client clears platform + metric in ONE patch (single-patch).
  function handleClientChange(nextClientId: string) {
    patch({ clientId: nextClientId, platform: "", metricKey: "" });
  }

  // ── Soft duplicate notice (non-blocking) ──────────────────────────────────
  const thresholdNum = Number(form.threshold);
  const isDuplicate = useMemo(() => {
    if (!form.clientId || !form.platform || !form.metricKey) return false;
    if (form.threshold.trim() === "" || Number.isNaN(thresholdNum)) return false;
    return existingAlerts.some(
      (a) =>
        a.id !== alert?.id &&
        a.clientId === form.clientId &&
        a.platform === form.platform &&
        a.metricKey === form.metricKey &&
        a.condition === form.condition &&
        a.interval === form.interval &&
        a.threshold === thresholdNum,
    );
  }, [
    existingAlerts,
    alert?.id,
    form.clientId,
    form.platform,
    form.metricKey,
    form.condition,
    form.interval,
    form.threshold,
    thresholdNum,
  ]);

  const mutation = useMutation({
    mutationFn: async () => {
      const payload = {
        clientId: form.clientId,
        name: form.name.trim(),
        platform: form.platform,
        metricKey: form.metricKey,
        condition: form.condition,
        threshold: Number(form.threshold),
        interval: form.interval,
      };
      const res = isEdit
        ? await apiPatch(`/api/alerts/${alert!.id}`, {
            name: payload.name,
            platform: payload.platform,
            metricKey: payload.metricKey,
            condition: payload.condition,
            threshold: payload.threshold,
            interval: payload.interval,
          })
        : await apiPost("/api/alerts", payload);
      const json = await res
        .json()
        .catch(() => ({ detail: "Server error. Please try again." }));
      if (!res.ok) throw { status: res.status, ...json };
      return json;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["alerts"] });
      onOpenChange(false);
      onSaved?.();
    },
    onError: (err) => {
      if (isUpgradeProblem(err)) {
        setGateModal(gateFromProblem(err));
      }
    },
  });

  function validate(): boolean {
    const e: Record<string, string> = {};
    if (!form.clientId) e.clientId = "Select a client";
    if (!form.platform) e.platform = "Select a platform";
    if (!form.metricKey) e.metricKey = "Select a metric";
    const t = Number(form.threshold);
    if (form.threshold.trim() === "" || Number.isNaN(t))
      e.threshold = "Enter a threshold value";
    if (!form.name.trim()) e.name = "Name is required";
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  function handleSubmit(ev: React.FormEvent) {
    ev.preventDefault();
    if (!validate()) return;

    // Client-side per-scope gate (CREATE only, paid tiers). EDIT never gates.
    // Uses the shared count algebra (demo-excluded, per-client) so the dialog
    // agrees with the page button and the server getAlertCount (27.4). The
    // server 403 fallback below still applies (defense-in-depth on a stale list).
    if (!isEdit && plan !== "free") {
      const clientsForGate = clients.map((c) => ({
        id: c.id,
        demoMode: !!c.demoMode,
      }));
      const count = countAlertsForGate(
        existingAlerts,
        clientsForGate,
        plan,
        form.clientId,
      );
      if (count !== undefined) {
        const gate = buildGate(plan, count);
        if (gate) {
          setGateModal(gate);
          return;
        }
      }
    }
    mutation.mutate();
  }

  function handleOpenChange(next: boolean) {
    if (!next && mutation.isPending) return; // close blocked while pending
    onOpenChange(next);
  }

  let fieldIndex = 0;
  const nextDelay = () => 0.05 * ++fieldIndex;

  return (
    <>
      <DialogPrimitive.Root open={open} onOpenChange={handleOpenChange}>
        <AnimatePresence>
          {open && (
            <DialogPrimitive.Portal forceMount>
              <DialogPrimitive.Overlay asChild forceMount>
                <motion.div
                  className="fixed inset-0 z-50 bg-black/35"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.25, ease: "easeOut" }}
                />
              </DialogPrimitive.Overlay>

              <DialogPrimitive.Content asChild forceMount>
                <motion.div
                  className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-3xl border-[0.5px] border-input bg-background p-8 shadow-lg max-h-[85vh] overflow-y-auto outline-none"
                  initial={{ opacity: 0, scale: 0.95, y: 10 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: 10 }}
                  transition={{ duration: 0.25, ease: "easeOut" }}
                >
                  <div className="flex flex-col space-y-1.5 text-left">
                    <DialogPrimitive.Title className="text-lg font-semibold leading-none tracking-tight">
                      {isEdit ? "Edit Alert" : "Create Alert"}
                    </DialogPrimitive.Title>
                    <DialogPrimitive.Description className="text-sm text-muted-foreground">
                      {isEdit
                        ? "Update this alert's metric, condition, and threshold."
                        : "Get notified when a client metric crosses a threshold."}
                    </DialogPrimitive.Description>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-4 mt-6">
                    {/* Client */}
                    <motion.div
                      className="space-y-2"
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: nextDelay() }}
                    >
                      <Label htmlFor="alert-client">Client</Label>
                      <DialogSelect
                        id="alert-client"
                        ariaLabel="Client"
                        value={form.clientId}
                        disabled={isEdit}
                        onChange={handleClientChange}
                        options={clientSelectOptions}
                        placeholder="Select a client…"
                        searchPlaceholder="Search clients…"
                      />
                      {errors.clientId && (
                        <p className="text-[12px] text-destructive">
                          {errors.clientId}
                        </p>
                      )}
                    </motion.div>

                    {/* Platform */}
                    <motion.div
                      className="space-y-2"
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: nextDelay() }}
                    >
                      <Label htmlFor="alert-platform">Platform</Label>
                      <DialogSelect
                        id="alert-platform"
                        ariaLabel="Platform"
                        value={form.platform}
                        onChange={(v) => patch({ platform: v, metricKey: "" })}
                        options={platformSelectOptions}
                        disabled={!effectiveClientId}
                        placeholder="Select a platform…"
                        searchPlaceholder="Search platforms…"
                      />
                      {errors.platform && (
                        <p className="text-[12px] text-destructive">
                          {errors.platform}
                        </p>
                      )}
                    </motion.div>

                    {/* Metric */}
                    <motion.div
                      className="space-y-2"
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: nextDelay() }}
                    >
                      <Label htmlFor="alert-metric">Metric</Label>
                      <DialogSelect
                        id="alert-metric"
                        ariaLabel="Metric"
                        value={form.metricKey}
                        onChange={(v) => patch({ metricKey: v })}
                        options={metricSelectOptions}
                        disabled={!form.platform}
                        placeholder="Select a metric…"
                        searchPlaceholder="Search metrics…"
                        searchable
                      />
                      {errors.metricKey && (
                        <p className="text-[12px] text-destructive">
                          {errors.metricKey}
                        </p>
                      )}
                    </motion.div>

                    {/* Condition + Threshold */}
                    <motion.div
                      className="grid grid-cols-2 gap-3"
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: nextDelay() }}
                    >
                      <div className="space-y-2">
                        <Label htmlFor="alert-condition">Condition</Label>
                        <DialogSelect
                          id="alert-condition"
                          ariaLabel="Condition"
                          value={form.condition}
                          onChange={(v) =>
                            patch({ condition: v as AlertCondition })
                          }
                          options={CONDITION_OPTIONS}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="alert-threshold">Threshold</Label>
                        <Input
                          id="alert-threshold"
                          type="number"
                          className="rounded-lg"
                          value={form.threshold}
                          onChange={(e) => patch({ threshold: e.target.value })}
                          placeholder="0"
                        />
                        {errors.threshold && (
                          <p className="text-[12px] text-destructive">
                            {errors.threshold}
                          </p>
                        )}
                      </div>
                    </motion.div>

                    {/* Interval */}
                    <motion.div
                      className="space-y-2"
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: nextDelay() }}
                    >
                      <Label htmlFor="alert-interval">Interval</Label>
                      <DialogSelect
                        id="alert-interval"
                        ariaLabel="Interval"
                        value={form.interval}
                        onChange={(v) => patch({ interval: v as AlertInterval })}
                        options={INTERVAL_OPTIONS}
                      />
                    </motion.div>

                    {/* Name (auto-generated, editable) */}
                    <motion.div
                      className="space-y-2"
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: nextDelay() }}
                    >
                      <Label htmlFor="alert-name">Alert name</Label>
                      <Input
                        id="alert-name"
                        className="rounded-lg"
                        value={form.name}
                        onChange={(e) => {
                          setNameDirty(true);
                          patch({ name: e.target.value });
                        }}
                        placeholder="e.g. Google Ads Cost Alert"
                      />
                      {errors.name && (
                        <p className="text-[12px] text-destructive">
                          {errors.name}
                        </p>
                      )}
                    </motion.div>

                    {/* Soft duplicate notice — non-blocking (27.1 decision #3) */}
                    {isDuplicate && (
                      <motion.div
                        className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-[12px] text-amber-700"
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: nextDelay() }}
                        role="status"
                      >
                        <Warning size={16} className="shrink-0 mt-0.5" />
                        <span>
                          You already have a similar alert with the same client,
                          platform, metric, condition, threshold, and interval.
                          You can still create it.
                        </span>
                      </motion.div>
                    )}

                    {mutation.isError && !isUpgradeProblem(mutation.error) && (
                      <p className="text-[12px] text-destructive" role="alert">
                        {(mutation.error as { detail?: string; title?: string })
                          ?.detail ??
                          (mutation.error as { title?: string })?.title ??
                          "Failed to save alert"}
                      </p>
                    )}

                    <div className="flex justify-end gap-3 mt-6">
                      <Button
                        type="button"
                        variant="outline"
                        className="rounded-2xl bg-background cursor-pointer shadow-xs hover:-translate-y-0.5 hover:shadow-sm hover:bg-background transition-all"
                        style={{ border: "0.5px solid var(--hairline)" }}
                        onClick={() => handleOpenChange(false)}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        variant="brand"
                        className="rounded-2xl text-[13px] cursor-pointer"
                        disabled={mutation.isPending}
                      >
                        {mutation.isPending
                          ? "Saving…"
                          : isEdit
                            ? "Save changes"
                            : "Create alert"}
                      </Button>
                    </div>
                  </form>
                </motion.div>
              </DialogPrimitive.Content>
            </DialogPrimitive.Portal>
          )}
        </AnimatePresence>
      </DialogPrimitive.Root>

      <UpgradeLimitModal
        open={gateModal !== null}
        onOpenChange={(o) => !o && setGateModal(null)}
        feature="alertLimit"
        currentPlan={gateModal?.currentPlan ?? plan}
        currentUsage={gateModal?.currentUsage ?? 0}
        currentLimit={gateModal?.currentLimit ?? 0}
        upgradePlan={gateModal?.upgradePlan}
        nextTierLimit={gateModal?.nextTierLimit ?? null}
        billingInterval="monthly"
      />
    </>
  );
}
